import React from 'react';
import { Helmet } from '@okapi/helmet-react';
import { useSelector } from 'react-redux';
import { memo } from 'common/hocs/memo';
import { getModelJson } from 'features/page-model/selectors';
import { getBaseUrl } from 'common/selectors';
import { getProductImages } from 'features/product-info';

export type PageMetaProps = {
  isDev: boolean;
};

export const PageMeta: React.FC<PageMetaProps> = memo(({ isDev }) => {
  const modelJson = useSelector(getModelJson);
  const currentUrl = useSelector(getBaseUrl);
  const images = useSelector(getProductImages);
  const opengraphProductImg = images && images[0]?.url;

  return modelJson ? (
    <>
      <Helmet>
        <title>{modelJson.seoProperties?.pageTitle}</title>
        <meta name="keywords" content={modelJson?.seoProperties.metakeywords ?? undefined} />
        <meta name="description" content={modelJson?.seoProperties.metadescription ?? undefined} />
        <link rel="canonical" href={currentUrl ?? undefined} />
        <meta property="og:title" content={modelJson?.seoProperties.pageTitle ?? undefined} />
        <meta property="og:description" content={modelJson?.seoProperties.metadescription ?? undefined} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={opengraphProductImg ?? undefined} />
        {modelJson?.seoProperties.canonicalURLNeeded ? (
          <meta property="og:url" content={modelJson?.seoProperties.canonicalURL ?? undefined} />
        ) : (
          <meta property="og:url" content={currentUrl} />
        )}
      </Helmet>
      {isDev ? (
        <Helmet>
          <link
            rel="icon"
            type="image/svg+xml"
            href="data:image/svg+xml,%3Csvg width='64' height='64' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill='%23fff' d='M0 0h64v64H0z'/%3E%3Cpath d='M7.7 49l3.6-3.6-1.2-1.2L8 46.4l-4.1-4-1.4 1.3L7.7 49zM9.2 39l1 1 1.8-2 1 1.2-1.8 1.8 1.1 1.2 2.3-2.3 1.1 1-3.6 3.7-5.3-5.3 3.6-3.7 1.1 1.1L9.2 39zM21 35.7l-3.6-1c.4-.9.2-2-.5-2.7-.8-.7-2.1-.7-3 .2l-2.7 2.7 5.3 5.3 1.3-1.4-4.2-4.3.8-.8a1 1 0 011.4 0c.4.3.4 1 0 1.3l-.8.8 4.6 1.3 1.4-1.4zM18.6 27.2c1.6-1.6 3.8-1.5 5.4.1 1.5 1.6 1.7 3.7 0 5.4-1.6 1.7-3.8 1.5-5.4 0-1.5-1.6-1.7-3.8 0-5.5zm1 1.1c-.8.8-.5 1.9.5 2.9s2 1.3 2.9.5c.8-.9.5-2-.5-3s-2-1.2-2.9-.4zM25.6 24.4l-1-3L26 20l1.4 5.1 2 2.1-1.3 1.3-2-2-5-1.6 1.5-1.5 2.8 1.1zM50 42.8l5.4-5.4-1.5-1.4-5.3 5.3 1.5 1.5zM29.8 22.4l4.1-2-3 3.1 1.2 1.2 5.3-5.3-1.7-1.7-4 2 2-4L32 14l-5.3 5.3 1.2 1.2 3-3-1.9 4.1.8.8zM39 23.2l-1 .9 2.1 2-1 1.1-2.1-2-1.2 1.1 2.5 2.5-1 1-3.9-3.7 5.3-5.4 3.8 3.9-1 1-2.5-2.4zM44 36.8l3.7 3.6 1.2-1.2-2.3-2.3 4.1-4-1.3-1.4-5.3 5.3zM58.3 40.5L56.7 46l3.6-3.7 1.3 1.3-5.3 5.3-1.7-1.7 1.5-5.8-3.7 3.6-1.2-1.2 5.3-5.3 1.8 1.9zM43 35.8l1.1-3.6c.8.4 1.9.2 2.6-.5.8-.8.8-2.2-.1-3l-2.9-3-5.3 5.4 1.4 1.3 4.2-4.3 1 1c.5.5.5 1 .1 1.4a1 1 0 01-1.4 0l-1-1-1 4.8 1.4 1.5zM8.7 50h46.6L32 26.6 8.7 50z' fill='%23333'/%3E%3C/svg%3E"
          />
        </Helmet>
      ) : (
        <Helmet>
          <link href={modelJson.header.faviconImage} rel="shortcut icon" type="image/x-icon" />
          <link
            rel="apple-touch-icon"
            href="https://cdn.lemanapro.ru/lmru/image/upload/v1/other/navigation-microfronts/favicon/touch-icon-iphone.jpg/file.jpg"
          />
          <link
            rel="apple-touch-icon"
            sizes="152x152"
            href="https://cdn.lemanapro.ru/lmru/image/upload/v1/other/navigation-microfronts/favicon/touch-icon-ipad-retina.jpg/file.jpg"
          />
          <link
            rel="apple-touch-icon"
            sizes="76x76"
            href="https://cdn.lemanapro.ru/lmru/image/upload/v1/other/navigation-microfronts/favicon/touch-icon-ipad.jpg/file.jpg"
          />
          <link
            rel="apple-touch-icon"
            sizes="120x120"
            href="https://cdn.lemanapro.ru/lmru/image/upload/v1/other/navigation-microfronts/favicon/touch-icon-iphone-retina.jpg/file.jpg"
          />
        </Helmet>
      )}
    </>
  ) : null;
});
